/**
 * industry-surfaces — the surface registry. THE scaling spine for Block C.
 *
 * One source of truth for "what navigable surfaces does a business have?",
 * composed from three sources (see Eaze_Phase2_BlockC_Spec.md §architecture):
 *
 *   core      — every business, always (POS, sales, inventory, + POS sheet tools)
 *   industry  — keyed off business.industryType (salon→appointments, pharmacy→batches…)
 *   module    — cross-industry, enabled per business (HR, payroll, procurement…)
 *
 * The cashier bottom-nav and the admin sidebar both render by calling
 * getSurfaces() — they do NOT hardcode their own tab lists. Adding a surface
 * to an industry = one entry here. Adding an industry = its entries here +
 * the page components. That's the whole extension model.
 *
 * STATUS DISCIPLINE: every surface carries status: "live" | "planned".
 * "live" = the route/sheet exists today. "planned" = designed, not built.
 * Nav renders live surfaces; planned ones are filtered out unless
 * includePlanned is passed (useful for a roadmap/settings view). This lets
 * the registry hold the FULL design now while the UI only shows what's real —
 * so we never ship a tab that 404s.
 *
 * LABELS: `label` is the static fallback. When `termKey` is set, the consuming
 * nav should prefer useT()(termKey) so the label flexes per industry
 * ("Orders" → "Appointments" → "Dispensary"). The registry stays terminology-
 * agnostic; the nav applies useT at render.
 */
import type { LucideIcon } from "lucide-react";
import {
  ShoppingCart,
  Receipt,
  Boxes,
  Calculator,
  Bookmark,
  CalendarCheck,
  Users,
  Scissors,
  UtensilsCrossed,
  ChefHat,
  Pill,
  CalendarClock,
  ShieldCheck,
  FileText,
  Truck,
  Briefcase,
  Dumbbell,
  ClipboardList,
  UserCog,
  Wallet,
  PackageSearch,
  Building2,
  FolderKanban,
  DollarSign,
  MessageSquare,
  Send,
  User,
  Banknote,
  Undo2,
  Timer,
  ClipboardCheck,
  CalendarDays,
} from "lucide-react";
import type {
  IndustryId,
  IndustryTerms,
  StringTermKey,
} from "@/constants/industries";

// ─── Vocabulary ─────────────────────────────────────────────────────

export type Role =
  | "tenant_owner"
  | "super_admin"
  | "business_admin"
  | "staff"
  | "cashier";

/** Which app surface a definition belongs to. */
export type AppSurface = "admin" | "cashier" | "both";

/** route = a full page reached by path. sheet = an in-POS overlay tool. */
export type SurfaceKind = "route" | "sheet";

export type SurfaceStatus = "live" | "planned";
export type SurfaceSection = "workspace" | "operations" | "account";

/** Cross-industry ERP modules, enabled per business (plan/flag gated). */
export type ModuleKey =
  | "hr"
  | "payroll"
  | "procurement"
  | "assets"
  | "projects";

export interface SurfaceDef {
  /** Stable id, unique across the whole registry. */
  key: string;
  /** Static fallback label. Prefer useT()(termKey) at render when termKey set. */
  label: string;
  /** When set, the nav pulls the label from a STRING industry term. Nested
   * terms (e.g. `schedule`) are intentionally excluded — surfaces that need a
   * nested label resolve it directly in the nav (see core.schedule). */
  termKey?: StringTermKey;
  icon: LucideIcon;
  kind: SurfaceKind;
  /** For kind "route". */
  path?: string;
  /** For kind "sheet" — an id the POS resolves to a sheet component. */
  sheetId?: string;
  app: AppSurface;
  roles: Role[];
  /** Sort order within its group; lower = earlier. */
  order: number;
  status: SurfaceStatus;
  /** If set, only these industries get this surface. Absent = all (core). */
  industries?: IndustryId[];
  /** If set, only when this module is enabled for the business. */
  module?: ModuleKey;
  /** Cashier-sidebar grouping. Which section this surface sits under in the
   * desktop cashier sidebar. Cashier-only hint — the admin sidebar injects
   * industry surfaces into Workspace regardless. Defaults to "operations". */
  section?: SurfaceSection;
  /** Live count badge resolved at render (currently team-chat unread). */
  badgeKey?: "chatUnread";
  /** Shown only when the person holds this permission (checked in the nav). */
  perm?: string;
}

// ─── Role groups ────────────────────────────────────────────────────

const CASHIER_AND_UP: Role[] = [
  "cashier",
  "business_admin",
  "super_admin",
  "tenant_owner",
];
const ADMINS: Role[] = ["business_admin", "super_admin", "tenant_owner"];
const TENANT_WIDE: Role[] = ["super_admin", "tenant_owner"];

// ─── 1. Core surfaces (every industry) ──────────────────────────────

const CORE_SURFACES: SurfaceDef[] = [
  {
    key: "core.pos",
    label: "Sell",
    icon: ShoppingCart,
    kind: "route",
    path: "/cashier",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 10,
    status: "live",
  },
  {
    key: "core.sales",
    label: "Sales",
    termKey: "ordersPageTitle",
    icon: Receipt,
    kind: "route",
    path: "/cashier/sales",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 20,
    status: "live",
  },
  {
    key: "core.returns",
    label: "My returns",
    icon: Undo2,
    kind: "route",
    path: "/cashier/returns",
    app: "cashier",
    roles: ["cashier"],
    order: 25,
    status: "live",
  },
  {
    key: "core.shift",
    label: "My shift",
    icon: Timer,
    kind: "route",
    path: "/cashier/shifts",
    app: "cashier",
    roles: ["cashier"],
    order: 27,
    status: "live",
  },
  {
    key: "core.stockcount",
    label: "Stock count",
    icon: ClipboardCheck,
    kind: "route",
    path: "/cashier/stock-take",
    app: "cashier",
    roles: ["cashier"],
    order: 28,
    status: "live",
  },
  {
    key: "core.inventory",
    label: "Stock",
    icon: Boxes,
    kind: "route",
    path: "/cashier/inventory",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 30,
    status: "live",
  },
  // ── Shared CUSTOMERS directory (every industry) ──────────────────
  // Formerly "gym.members" (gym-only, planned). We built one industry-flexed
  // directory at /customers that works for all industries: the label flexes
  // via termKey "customers" (Members / Clients / Customers per industry), and
  // for gym it gains membership management + check-in history. Promoted to a
  // core surface so retail, salon, gym, etc. all get it. app:"both" → shows in
  // both the admin sidebar and the cashier nav.
  {
    key: "core.customers",
    label: "Customers",
    termKey: "customers",
    icon: Users,
    kind: "route",
    path: "/customers",
    app: "both",
    roles: CASHIER_AND_UP,
    order: 55,
    status: "live",
    perm: "customer.read",
    section: "operations",
  },
  // ── Messaging (cross-industry admin tool) ────────────────────────
  // Bulk SMS (Arkesel) + email to member segments. Admin-only; not POS, not
  // industry-specific → a core admin surface.
  {
    key: "core.messaging",
    label: "Messaging",
    icon: Send,
    kind: "route",
    path: "/messaging",
    app: "admin",
    roles: ADMINS,
    order: 75,
    status: "live",
    perm: "campaign.read",
  },
  // ── Secondary cashier surfaces (route-kind; sidebar + bottom-nav "More") ──
  {
    key: "core.expenditure",
    label: "Expenditure",
    icon: DollarSign,
    kind: "route",
    path: "/cashier/expenditure",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 60,
    status: "live",
  },
  {
    key: "core.chat",
    label: "Team Chat",
    icon: MessageSquare,
    kind: "route",
    path: "/cashier/chat",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 70,
    status: "live",
    section: "account",
    badgeKey: "chatUnread",
  },
  {
    key: "core.myHr",
    label: "Leave & payslips",
    icon: CalendarDays,
    kind: "route",
    path: "/cashier/my-hr",
    app: "cashier",
    roles: ["cashier"],
    order: 75,
    status: "live",
    section: "account",
    perm: "employee.read.own",
  },
  {
    key: "core.account",
    label: "My Account",
    icon: User,
    kind: "route",
    path: "/cashier/account",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 80,
    status: "live",
    section: "account",
  },

  // ── In-POS sheet tools (built this block) ──
  {
    key: "core.calculator",
    label: "Calculator",
    icon: Calculator,
    kind: "sheet",
    sheetId: "calculator",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 40,
    status: "live",
  },
  {
    key: "core.savedCarts",
    label: "Saved",
    icon: Bookmark,
    kind: "sheet",
    sheetId: "savedCarts",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 50,
    status: "live",
  },
];

// ─── 2. Industry surfaces (by business.industryType) ────────────────
// All "planned" until their page components land (C1+). The registry holds
// the full design now; nav shows them only as each is built.

const INDUSTRY_SURFACES: SurfaceDef[] = [
  // ── Generic SCHEDULE surface (C1 — first LIVE industry build) ──────
  // ONE surface, shared by every scheduling industry. The displayed label
  // flexes per industry at render: the nav reads industry.terms.schedule.title
  // ("Appointments" / "Reservations" / "Bookings" / "Classes" / "Deliveries").
  // termKey below ("ordersPageTitle") is only the registry-level fallback for
  // any nav that doesn't yet special-case schedule terms. Industries with NO
  // schedule term (retail, hardware, pharmacy, other) are simply absent from
  // the `industries` list, so they never get this surface. Backed by scheduled
  // Orders (isScheduled + deliveryDate) — no per-industry page.
  {
    key: "core.schedule",
    label: "Schedule",
    // termKey: "ordersPageTitle",
    icon: CalendarCheck,
    kind: "route",
    path: "/cashier/schedule",
    app: "both",
    roles: CASHIER_AND_UP,
    order: 35,
    status: "live",
    // gym intentionally excluded: a gym's "schedule" is its weekly class
    // timetable (gym.classes → /classes), not the one-to-one scheduled-order
    // rail. Including it here would collide with gym's terms.schedule.title
    // ("Classes") and produce two "Classes" nav items.
    industries: ["salon", "restaurant", "services", "wholesale"],
  },

  // salon
  {
    key: "salon.clients",
    label: "Clients",
    termKey: "customers",
    icon: Users,
    kind: "route",
    path: "/clients",
    app: "both",
    roles: CASHIER_AND_UP,
    order: 120,
    status: "planned",
    industries: ["salon"],
  },
  // Stylist roster + per-stylist activity (built). Serves salon AND services
  // (both assign work to staff). Repointed from the never-built
  // "/staff-commissions" to the live "/stylists" page.
  {
    key: "salon.staff",
    label: "Stylists",
    icon: Scissors,
    kind: "route",
    path: "/stylists",
    app: "admin",
    roles: ADMINS,
    order: 130,
    status: "live",
    industries: ["salon", "services"],
  },

  // restaurant
  {
    key: "restaurant.tables",
    label: "Tables",
    icon: UtensilsCrossed,
    kind: "route",
    path: "/cashier/tables",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 110,
    status: "planned",
    industries: ["restaurant"],
  },
  {
    key: "restaurant.kitchen",
    label: "Kitchen",
    icon: ChefHat,
    kind: "route",
    path: "/cashier/kitchen",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 120,
    status: "planned",
    industries: ["restaurant"],
  },

  // pharmacy
  {
    key: "pharmacy.prescriptions",
    label: "Prescriptions",
    icon: Pill,
    kind: "route",
    path: "/cashier/prescriptions",
    app: "both",
    roles: CASHIER_AND_UP,
    order: 110,
    status: "planned",
    industries: ["pharmacy"],
  },
  {
    key: "pharmacy.batches",
    label: "Batches & expiry",
    icon: CalendarClock,
    kind: "route",
    path: "/batches",
    app: "both",
    roles: CASHIER_AND_UP,
    order: 120,
    status: "planned",
    industries: ["pharmacy"],
  },
  {
    key: "pharmacy.controlled",
    label: "Controlled log",
    icon: ShieldCheck,
    kind: "route",
    path: "/controlled-log",
    app: "admin",
    roles: ADMINS,
    order: 130,
    status: "planned",
    industries: ["pharmacy"],
  },

  // hardware
  {
    key: "hardware.quotes",
    label: "Quotes & orders",
    icon: FileText,
    kind: "route",
    path: "/sales-orders",
    app: "both",
    roles: CASHIER_AND_UP,
    order: 110,
    status: "planned",
    industries: ["hardware"],
  },

  // wholesale
  {
    key: "wholesale.orders",
    label: "Sales orders",
    icon: Truck,
    kind: "route",
    path: "/sales-orders",
    app: "both",
    roles: CASHIER_AND_UP,
    order: 110,
    status: "planned",
    industries: ["wholesale"],
  },

  // services
  {
    key: "services.bookings",
    label: "Bookings",
    icon: CalendarCheck,
    kind: "route",
    path: "/cashier/bookings",
    app: "both",
    roles: CASHIER_AND_UP,
    order: 110,
    status: "planned",
    industries: ["services"],
  },
  {
    key: "services.jobs",
    label: "Jobs",
    termKey: "ordersPageTitle",
    icon: Briefcase,
    kind: "route",
    path: "/jobs",
    app: "both",
    roles: CASHIER_AND_UP,
    order: 120,
    status: "planned",
    industries: ["services"],
  },

  // gym
  // NOTE: the gym member directory is now the shared core.customers surface
  // (above) — the old gym-only "gym.members" entry was promoted to core, so
  // it is intentionally removed from here to avoid a duplicate nav item.
  {
    key: "gym.checkin",
    label: "Check-in",
    icon: CalendarCheck,
    kind: "route",
    path: "/cashier/checkin",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 120,
    status: "live",
    industries: ["gym"],
  },
  // Weekly class timetable (built). Admin-only. This IS the gym's schedule, so
  // core.schedule excludes gym (above) and this owns the "Classes" label
  // cleanly — no collision.
  {
    key: "gym.classes",
    label: "Classes",
    icon: CalendarClock,
    kind: "route",
    path: "/classes",
    app: "admin",
    roles: ADMINS,
    order: 130,
    status: "live",
    industries: ["gym"],
  },
  // construction
  {
    key: "construction.projects",
    label: "Projects",
    icon: FolderKanban,
    kind: "route",
    path: "/projects",
    app: "admin",
    roles: ADMINS,
    order: 110,
    status: "live",
    industries: ["construction"],
  },
  // construction — cashier payment collection (draws + unit installments).
  // Cashier counterpart to the admin Projects surface. Backend pay routes are
  // TENANT_ALL after the /projects mount change. app:"cashier" → renders only
  // in the cashier nav (sidebar v7 + bottom nav read the same getSurfaces).
  {
    key: "construction.collect",
    label: "Collect",
    icon: Wallet,
    kind: "route",
    path: "/cashier/collect",
    app: "cashier",
    roles: CASHIER_AND_UP,
    order: 25,
    status: "live",
    section: "operations",
    industries: ["construction"],
  },

  // construction — admin collections (draws / installments / retention the
  // cashier recorded). app:"admin" → admin nav only; owner sees a group toggle
  // (backend enforces scope). Pairs with construction.collect (cashier side).
  {
    key: "construction.collections",
    label: "Collections",
    icon: Banknote,
    kind: "route",
    path: "/collections",
    app: "admin",
    roles: ADMINS,
    order: 115,
    status: "live",
    industries: ["construction"],
  },
];

// ─── 3. Module surfaces (cross-industry, flag-gated) ────────────────
// Industry-agnostic ERP. Appear for ANY industry once the module is enabled
// for the business. All planned. Payroll is the high-value GH-SMB first slice
// (PAYE/SSNIT) — still pull-based.

const MODULE_SURFACES: SurfaceDef[] = [
  {
    key: "module.hr",
    label: "HR",
    icon: ClipboardList,
    kind: "route",
    path: "/hr",
    app: "admin",
    roles: ADMINS,
    order: 210,
    status: "planned",
    module: "hr",
  },
  {
    key: "module.payroll",
    label: "Payroll",
    icon: Wallet,
    kind: "route",
    path: "/payroll",
    app: "admin",
    roles: TENANT_WIDE,
    order: 220,
    status: "planned",
    module: "payroll",
  },
  {
    key: "module.procurement",
    label: "Procurement",
    icon: PackageSearch,
    kind: "route",
    path: "/procurement",
    app: "admin",
    roles: ADMINS,
    order: 230,
    status: "planned",
    module: "procurement",
  },
  {
    key: "module.assets",
    label: "Assets",
    icon: Building2,
    kind: "route",
    path: "/assets",
    app: "admin",
    roles: ADMINS,
    order: 240,
    status: "planned",
    module: "assets",
  },
  {
    key: "module.projects",
    label: "Projects",
    icon: FolderKanban,
    kind: "route",
    path: "/projects",
    app: "admin",
    roles: ADMINS,
    order: 250,
    status: "planned",
    module: "projects",
  },
];

export const ALL_SURFACES: SurfaceDef[] = [
  ...CORE_SURFACES,
  ...INDUSTRY_SURFACES,
  ...MODULE_SURFACES,
];

// ─── Selector ───────────────────────────────────────────────────────

export interface GetSurfacesOptions {
  industry: IndustryId;
  /** "admin" or "cashier" — the app asking. "both" surfaces match either. */
  app: "admin" | "cashier";
  role: Role;
  /** Modules turned on for this business. Defaults to none. */
  enabledModules?: ModuleKey[];
  /** Include "planned" surfaces (e.g. for a roadmap view). Default false. */
  includePlanned?: boolean;
  /** Include sheet-kind tools. Default true. */
  includeSheets?: boolean;
}

/**
 * Compose the ordered list of surfaces for one business context. Pure —
 * the useSurfaces() hook wraps it with live store reads.
 */
export function getSurfaces(opts: GetSurfacesOptions): SurfaceDef[] {
  const {
    industry,
    app,
    role,
    enabledModules = [],
    includePlanned = false,
    includeSheets = true,
  } = opts;

  return ALL_SURFACES.filter((s) => {
    if (!includePlanned && s.status !== "live") return false;
    if (!includeSheets && s.kind === "sheet") return false;

    // App match: "both" matches either app.
    if (s.app !== "both" && s.app !== app) return false;

    // Role match.
    if (!s.roles.includes(role)) return false;

    // Industry gate: absent = core (all industries).
    if (s.industries && !s.industries.includes(industry)) return false;

    // Module gate.
    if (s.module && !enabledModules.includes(s.module)) return false;

    return true;
  }).sort((a, b) => a.order - b.order);
}
